import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Button } from "../ui/button";
import { MEDIA } from "../../constants/media";
import { VideoIntro } from "../VideoIntro";
import { UserPlus, FileText, Send, BellRing, PenLine, Wand2, CreditCard, Users, ArrowRight } from "lucide-react";

const VIDEO_CANDIDATE_URL = process.env.REACT_APP_VIDEO_CANDIDATE_URL || "";
const VIDEO_COMPANY_URL = process.env.REACT_APP_VIDEO_COMPANY_URL || "";

const CANDIDATE_STEPS = [
  {
    icon: UserPlus, title: "Cria conta grátis",
    desc: "Registo em menos de um minuto, com consentimento RGPD explícito. Sem custos, para sempre.",
    substeps: [
      "Escolhe candidato no formulário de registo",
      "Confirma que aceitas termos e privacidade",
      "Recebes um código de convite pessoal para partilhar",
    ],
  },
  {
    icon: FileText, title: "Perfil e CV",
    desc: "Carrega o CV em PDF (fica privado) e deixa a IA preencher o teu perfil e sugerir melhorias.",
    substeps: [
      "Upload de PDF até 5 MB (fica em armazenamento privado)",
      "IA extrai competências, pontos fortes e palavras-chave em falta",
      "Um clique preenche o perfil a partir do CV",
    ],
  },
  {
    icon: Send, title: "Candidata-te em 2 minutos",
    desc: "Um clique por vaga. Vês o match com a vaga antes de te candidatares.",
    substeps: [
      "Filtra por distrito, concelho, categoria e distância em km",
      "Vê o match score 0-100 no detalhe da vaga",
      "Guarda favoritos para veres mais tarde",
    ],
  },
  {
    icon: BellRing, title: "Acompanha o estado",
    desc: "Submetida, em análise, entrevista, aceite ou rejeitada — sempre atualizado no teu painel.",
    substeps: [
      "Recebe alertas de novas vagas por distrito e categoria",
      "Estado da candidatura atualiza em tempo real",
      "Convida 3 amigos e ganha 30 dias de IA ilimitada",
    ],
  },
];

const COMPANY_STEPS = [
  {
    icon: PenLine, title: "Registo da empresa",
    desc: "Cria a conta e o teu perfil público de empresa, com página indexável no Google.",
    substeps: [
      "Preenche setor, dimensão, cultura e benefícios",
      "URL público em first2job.pt/empresas/o-teu-nome",
      "Espera pela aprovação da administração (rápida)",
    ],
  },
  {
    icon: Wand2, title: "Publica com ajuda da IA",
    desc: "Wizard de 3 passos: escreve palavras-chave e a IA gera título, descrição e requisitos.",
    substeps: [
      "Passo 1 — palavras-chave e distrito/categoria",
      "Passo 2 — a IA gera o anúncio, tu editas",
      "Passo 3 — moderação e vai para o mural em minutos",
    ],
  },
  {
    icon: CreditCard, title: "Destaca (opcional)",
    desc: "3 tiers de destaque avulso: EXPRESS 15€, SILVER 45€ e PREMIUM GOLD 99€. Packs de créditos com desconto.",
    substeps: [
      "EXPRESS 15€ · 5 dias · urgência para preencher já",
      "SILVER 45€ · 10 dias · o equilíbrio popular",
      "PREMIUM GOLD 99€ · 30 dias · prestígio total",
    ],
  },
  {
    icon: Users, title: "Recebe e tria candidatos",
    desc: "Match score por candidato, triagem em massa com IA e estados de candidatura.",
    substeps: [
      "Vê CV, perfil e match score de cada candidato",
      "Triagem em massa (até 5 candidaturas por chamada)",
      "Atualiza estado: em análise → entrevista → aceite/rejeitada",
    ],
  },
];

const Timeline = ({ steps, testid }) => (
  <ol className="relative grid md:grid-cols-2 gap-5" data-testid={testid}>
    {steps.map((s, i) => (
      <li key={s.title} className="group relative bg-white rounded-2xl border border-black/5 p-7 hover:border-amber-300 hover:shadow-lg transition-colors">
        <div className="flex items-start justify-between">
          <div className="w-11 h-11 rounded-xl bg-slate-900 flex items-center justify-center group-hover:bg-amber-500 transition-colors">
            <s.icon className="w-5 h-5 text-amber-400 group-hover:text-white transition-colors" strokeWidth={1.75} />
          </div>
          <span className="font-heading font-black text-4xl text-slate-100 group-hover:text-amber-100 transition-colors leading-none">
            {String(i + 1).padStart(2, "0")}
          </span>
        </div>
        <h3 className="font-heading font-bold tracking-tighter text-lg text-slate-900 mt-5">{s.title}</h3>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.desc}</p>
        {s.substeps && (
          <ul className="mt-4 space-y-1.5 border-t border-black/5 pt-4">
            {s.substeps.map((sub) => (
              <li key={sub} className="flex items-start gap-2 text-[13px] text-slate-600">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-amber-500 shrink-0" aria-hidden="true" />
                <span>{sub}</span>
              </li>
            ))}
          </ul>
        )}
      </li>
    ))}
  </ol>
);

export const HowItWorks = () => {
  const navigate = useNavigate();
  return (
    <section id="como-funciona" className="max-w-7xl mx-auto px-4 sm:px-8 py-20 scroll-mt-20">
      <div className="max-w-2xl">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600 mb-3">Passo a passo</p>
        <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl text-slate-900">Como funciona</h2>
        <p className="text-slate-600 mt-4">
          Dois caminhos, o mesmo objetivo: pôr as pessoas certas nos empregos certos, sem burocracia.
        </p>
      </div>

      <Tabs defaultValue="candidatos" className="mt-10">
        <TabsList className="bg-white border border-black/5 rounded-full p-1 h-auto">
          <TabsTrigger data-testid="how-tab-candidatos" value="candidatos" className="rounded-full px-6 py-2.5 text-sm data-[state=active]:bg-slate-900 data-[state=active]:text-white">
            Sou candidato
          </TabsTrigger>
          <TabsTrigger data-testid="how-tab-empresas" value="empresas" className="rounded-full px-6 py-2.5 text-sm data-[state=active]:bg-slate-900 data-[state=active]:text-white">
            Sou empresa
          </TabsTrigger>
        </TabsList>

        <TabsContent value="candidatos" className="mt-8">
          <div className="mb-8">
            <VideoIntro
              testId="video-candidato"
              poster={MEDIA.heroWorker}
              title="Da inscrição à entrevista — em 3 minutos"
              badge="Para candidatos"
              duration="30s"
              videoUrl={VIDEO_CANDIDATE_URL}
            />
          </div>
          <div className="grid lg:grid-cols-3 gap-6 items-start">
            <div className="lg:col-span-2">
              <Timeline steps={CANDIDATE_STEPS} testid="steps-candidato" />
            </div>
            <div className="rounded-2xl overflow-hidden border border-black/5 shadow-sm bg-white">
              <img src={MEDIA.warehouse} alt="Trabalhador de armazém em funções" className="w-full h-56 object-cover" loading="lazy" />
              <div className="p-6">
                <p className="text-sm font-semibold text-slate-900">O teu CV nunca fica público</p>
                <p className="text-sm text-slate-600 mt-2">
                  Só tu, o administrador e as empresas a que te candidatas conseguem abrir o teu PDF. Podes substituir ou apagar quando quiseres.
                </p>
                <Button data-testid="how-cta-candidato" className="mt-5 rounded-full w-full bg-slate-900 hover:bg-slate-800"
                  onClick={() => navigate("/auth?tab=register")}>
                  Criar conta de candidato <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="empresas" className="mt-8">
          <div className="mb-8">
            <VideoIntro
              testId="video-empresa"
              poster={MEDIA.recruiter}
              title="Publicar uma vaga com IA — em 3 minutos"
              badge="Para empresas"
              duration="30s"
              videoUrl={VIDEO_COMPANY_URL}
            />
          </div>
          <div className="grid lg:grid-cols-3 gap-6 items-start">
            <div className="lg:col-span-2">
              <Timeline steps={COMPANY_STEPS} testid="steps-empresa" />
            </div>
            <div className="rounded-2xl overflow-hidden border border-black/5 shadow-sm bg-white">
              <img src={MEDIA.recruiter} alt="Recrutador a analisar candidaturas no portátil" className="w-full h-56 object-cover" loading="lazy" />
              <div className="p-6">
                <p className="text-sm font-semibold text-slate-900">Publicar é grátis</p>
                <p className="text-sm text-slate-600 mt-2">
                  Pagas só se quiseres visibilidade extra. Cada vaga passa por moderação para manter a qualidade do mural.
                </p>
                <Button data-testid="how-cta-empresa" className="mt-5 rounded-full w-full bg-amber-500 hover:bg-amber-600 text-white"
                  onClick={() => navigate("/auth?tab=register")}>
                  Publicar uma vaga <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </section>
  );
};
